import React, { useState, useEffect } from "react";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import {
  AiOutlineDashboard,
  AiOutlineShoppingCart,
  AiOutlineUser,
  AiOutlineBgColors,
  AiOutlineLogout,
} from "react-icons/ai";

import { RiCouponLine } from "react-icons/ri";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { ImBlog } from "react-icons/im";
import { IoIosNotifications, IoMdSunny, IoMdMoon } from "react-icons/io";
import { FaClipboardList, FaBloggerB, FaUser } from "react-icons/fa";
import { SiBrandfolder } from "react-icons/si";
import { BiCategoryAlt } from "react-icons/bi";
import { Layout, Menu, theme } from "antd";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import axios from 'axios';

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { darkMode, toggleTheme } = useTheme();
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const navigate = useNavigate();
  
  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch (e) {
      return true;
    }
  };

  // Auto logout function that mimics manual logout
  const autoLogout = () => {
    localStorage.clear();
    window.location.reload();
  };

  // Add axios interceptor to handle 401 errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Check if it's a 401 error indicating session expiration
        if (error.response?.status === 401) {
          // Check if it's specifically a token expiration or session invalidation error
          const message = error.response?.data?.message || '';
          if (message.includes('Access token expired') || 
              message.includes('Session invalidated') ||
              message.includes('Session expired')) {
            autoLogout();
          }
        }
        return Promise.reject(error);
      }
    );

    // Clean up interceptor
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Periodically check for token expiration
  useEffect(() => {
    // Get token from localStorage
    const getTokenFromLocalStorage = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;

    // Only run if user is authenticated
    if (getTokenFromLocalStorage && getTokenFromLocalStorage.token) {
      // Check immediately on mount
      if (isTokenExpired(getTokenFromLocalStorage.token)) {
        autoLogout();
        return;
      }

      // Set up interval to check every minute
      const interval = setInterval(() => {
        if (isTokenExpired(getTokenFromLocalStorage.token)) {
          autoLogout();
        }
      }, 60000); // Check every minute

      // Clean up interval on unmount
      return () => clearInterval(interval);
    }
  }, []);
  
  return (
     <Layout /* onContextMenu={(e) => e.preventDefault()} */ className="min-h-screen">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        className="bg-gradient-to-b from-primary-light to-yellow-500  dark:from-gray-800 dark:to-gray-900"
      >
        <div className="logo py-4 flex items-center justify-center">
          <h2 className="text-white text-center text-xl font-bold mb-0">
            <span className="sm-logo md:hidden">VA</span>
            <span className="lg-logo hidden md:block"> VEDA Admin</span>
          </h2>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          defaultSelectedKeys={[]}
          className="bg-transparent dark:bg-transparent border-none text-white dark:text-white [&_.ant-menu-item]:text-white [&_.ant-menu-submenu-title]:text-white [&_.ant-menu-item:hover]:text-white [&_.ant-menu-submenu-title:hover]:text-white"
          onClick={({ key }) => {
            // Collapse sidebar on mobile after selection
            if (window.innerWidth < 768) {
              setCollapsed(true);
            }
            if (key === "signout") {
              localStorage.clear();
              window.location.reload();
            } else {
              navigate(key);
            }
          }}
          items={[
            {
              key: "",
              icon: <AiOutlineDashboard className="fs-4 text-white" />,
              label: "Dashboard",
            },
            {
              key: "customers",
              icon: <AiOutlineUser className="fs-4 text-white" />,
              label: "Customers",
            },
            {
              key: "Catalog",
              icon: <AiOutlineShoppingCart className="fs-4 text-white" />,
              label: "Catalog",
              children: [
                {
                  key: "catalog",
                  icon: <AiOutlineShoppingCart className="fs-4 text-white" />,
                  label: "Catalog",
                },
              ],
            },
            {
              key: "orders",
              icon: <FaClipboardList className="fs-4 text-white" />,
              label: "Orders",
            },
            {
              key: "process-preorder",
              icon: <FaClipboardList className="fs-4 text-white" />,
              label: "Process Preorders",
            },
            {
              key: "marketing",
              icon: <RiCouponLine className="fs-4 text-white" />,
              label: "Marketing",
              children: [
                {
                  key: "coupon",
                  icon: <ImBlog className="fs-4 text-white" />,
                  label: "Add Coupon",
                },
                {
                  key: "coupon-list",
                  icon: <RiCouponLine className="fs-4 text-white" />,
                  label: "Coupon List",
                },
                {
                  key: "mailchimp-campaigns",
                  icon: <ImBlog className="fs-4 text-white" />,
                  label: "Mailchimp Campaigns",
                },
              ],
            },
            {
              key: "blogs",
              icon: <FaBloggerB className="fs-4 text-white" />,
              label: "Blogs",
              children: [
                {
                  key: "blog",
                  icon: <ImBlog className="fs-4 text-white" />,
                  label: "Add Blog",
                },
                {
                  key: "blog-list",
                  icon: <FaBloggerB className="fs-4 text-white" />,
                  label: "Blog List",
                },
                {
                  key: "blog-category",
                  icon: <ImBlog className="fs-4 text-white" />,
                  label: "Add Blog Category",
                },
                {
                  key: "blog-category-list",
                  icon: <FaBloggerB className="fs-4 text-white" />,
                  label: "Blog Category List",
                },
              ],
            },
            {
              key: "temples-section",
              icon: <BiCategoryAlt className="fs-4 text-white" />,
              label: "Temples",
              children: [
                {
                  key: "temple",
                  icon: <BiCategoryAlt className="fs-4 text-white" />,
                  label: "Add Temple",
                },
                {
                  key: "temples-list",
                  icon: <BiCategoryAlt className="fs-4 text-white" />,
                  label: "Temples List",
                },
              ],
            },
            {
              key: "enquiries",
              icon: <FaClipboardList className="fs-4 text-white" />,
              label: "Enquiries",
            },
            {
              key: "signout",
              icon: <AiOutlineLogout className="fs-4 text-white" />,
              label: "Sign Out",
            },
          ]}
        />
      </Sider>
      <Layout className="site-layout bg-background-light dark:bg-background-dark">
        <Header
          className="flex justify-between items-center ps-1 pe-5 shadow dark:shadow-gray-800 dark:bg-background-dark md:ps-4"
          style={{
            padding: 0,
            background: colorBgContainer,
          }}
        >
          {React.createElement(
            collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
            {
              className: "trigger text-text-light dark:text-text-dark hover:text-primary-light dark:hover:text-primary-dark",
              onClick: () => setCollapsed(!collapsed),
            }
          )}
          <div className="flex gap-2 md:gap-4 items-center">
            <button 
              className="theme-toggle p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 transform hover:scale-110"
              onClick={toggleTheme}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? (
                <IoMdSunny size={24} className="text-yellow-400" />
              ) : (
                <IoMdMoon size={24} className="text-gray-700" />
              )}
            </button>
            <div className="flex gap-2 md:gap-3 items-center dropdown">
              <div className="rounded-full bg-primary-light dark:bg-primary-dark p-2">
                 <FaUser size={20} className="text-white" />
              </div>
              <div
                role="button"
                id="dropdownMenuLink"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                className="text-text-light dark:text-text-dark cursor-pointer hidden sm:block"
              >
                <h5 className="mb-0 font-semibold text-sm md:text-base">Admin</h5>
                <p className="mb-0 text-xs text-gray-500 dark:text-gray-300 hidden md:block">admin@gmail.com</p>
              </div>
              <div className="dropdown-menu bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg" aria-labelledby="dropdownMenuLink">
                <li>
                  <Link
                    className="dropdown-item py-2 mb-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-text-light dark:text-text-dark rounded-md px-4"
                    style={{ height: "auto", lineHeight: "20px" }}
                    to="/"
                  >
                    View Profile
                  </Link>
                </li>
                <li>
                  <Link
                    className="dropdown-item py-2 mb-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-text-light dark:text-text-dark rounded-md px-4"
                    style={{ height: "auto", lineHeight: "20px" }}
                    to="/"
                  >
                    Signout
                  </Link>
                </li>
              </div>
            </div>
          </div>
        </Header>
        <Content
          style={{
            margin: "24px 16px",
            padding: 24,
            minHeight: 280,
            background: colorBgContainer,
          }}
          className="bg-white dark:bg-surface-dark rounded-xl shadow dark:shadow-gray-800"
        >
          <ToastContainer
            position="top-right"
            autoClose={250}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            theme={darkMode ? "dark" : "light"}
          />
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;